import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, MessageCircle, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import NewsletterSignup from "@/components/NewsletterSignup";

// TripAdvisor doesn't ship in lucide — inline the SVG (owl mark).
function TripAdvisorIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12 2.5C6.75 2.5 2.5 6.4 2.5 11.2c0 1.28.32 2.5.9 3.6L2 17.5h5.16c1.36 1 3.03 1.6 4.84 1.6s3.48-.6 4.84-1.6H22l-1.4-2.7c.58-1.1.9-2.32.9-3.6C21.5 6.4 17.25 2.5 12 2.5zm-5.03 12.6c-1.9 0-3.44-1.6-3.44-3.55S5.07 8 6.97 8s3.44 1.6 3.44 3.55-1.54 3.55-3.44 3.55zm10.06 0c-1.9 0-3.44-1.6-3.44-3.55S15.13 8 17.03 8s3.44 1.6 3.44 3.55-1.54 3.55-3.44 3.55zM6.97 9.85c-.94 0-1.7.8-1.7 1.7s.76 1.7 1.7 1.7 1.7-.75 1.7-1.7-.76-1.7-1.7-1.7zm10.06 0c-.94 0-1.7.8-1.7 1.7s.76 1.7 1.7 1.7 1.7-.75 1.7-1.7-.76-1.7-1.7-1.7z" />
    </svg>
  );
}

const SOCIALS = [
  { href: "https://www.facebook.com/tourtripsindiaagra", Icon: Facebook, label: "Facebook", testid: "footer-facebook" },
  { href: "https://www.instagram.com/tourtripsindiaholidays/", Icon: Instagram, label: "Instagram", testid: "footer-instagram" },
  { href: "https://www.linkedin.com/in/tourtripsindiaholidays/", Icon: Linkedin, label: "LinkedIn", testid: "footer-linkedin" },
  { href: "https://www.youtube.com/@tourtripsindiaholidays", Icon: Youtube, label: "YouTube", testid: "footer-youtube" },
  { href: "https://www.tripadvisor.in/Attraction_Review-g297683-d12391310-Reviews-Tour_Trips_India_Holidays-Agra_Agra_District_Uttar_Pradesh.html", Icon: TripAdvisorIcon, label: "TripAdvisor", testid: "footer-tripadvisor" },
];

export default function Footer() {
  return (
    <footer className="bg-peacock text-cream relative overflow-hidden" data-testid="footer">
      <div className="absolute inset-0 mandala-bg pointer-events-none" />

      {/* NEWSLETTER BAND */}
      <div className="border-b border-white/10 relative">
        <div className="container-tti py-14">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="eyebrow text-marigold mb-3">Newsletter</div>
              <h3 className="font-serif text-3xl md:text-4xl text-white tracking-tight leading-tight">
                Travel stories, off-beat tips & exclusive offers — in your inbox once a month.
              </h3>
            </div>
            <div className="lg:col-span-6">
              <NewsletterSignup variant="footer" />
            </div>
          </div>
        </div>
      </div>

      <div className="container-tti py-20 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="mb-6">
              <img
                src={LOGO_URL}
                alt="Tour Trips India Holidays"
                className="h-24 w-auto object-contain"
              />
            </div>
            <p className="text-cream/80 leading-relaxed max-w-md font-light">
              Curating soulful, design-led journeys through Incredible India — and beyond to Nepal, Dubai and Thailand.
              Family-run from the city of the Taj.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              {SOCIALS.map(({ href, Icon, label, testid }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-sm bg-white/10 hover:bg-saffron flex items-center justify-center transition-all"
                  aria-label={label}
                  data-testid={testid}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
              <a
                href="https://wa.me/917017798524"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-white/10 hover:bg-saffron flex items-center justify-center transition-all"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="eyebrow text-marigold mb-5">Quick Links</div>
            <ul className="space-y-3 text-sm font-light text-cream/85">
              <li><Link to="/tours" className="hover:text-marigold">All Tours</Link></li>
              <li><Link to="/destinations" className="hover:text-marigold">All Destinations</Link></li>
              <li><Link to="/tours?category=golden-triangle" className="hover:text-marigold">Golden Triangle</Link></li>
              <li><Link to="/tours?category=rajasthan" className="hover:text-marigold">Rajasthan Heritage</Link></li>
              <li><Link to="/tours?category=spiritual" className="hover:text-marigold">Spiritual India</Link></li>
              <li><Link to="/tours?category=wildlife" className="hover:text-marigold">Wildlife Safaris</Link></li>
              <li><Link to="/services" className="hover:text-marigold">Our Services</Link></li>
              <li><Link to="/about" className="hover:text-marigold">About Us</Link></li>
            </ul>

            <div className="eyebrow text-marigold mb-3 mt-8">Legal</div>
            <ul className="space-y-2 text-sm font-light text-cream/85">
              <li><Link to="/terms" className="hover:text-marigold" data-testid="footer-link-terms">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-marigold" data-testid="footer-link-privacy">Privacy Policy</Link></li>
              <li><Link to="/cancellation" className="hover:text-marigold" data-testid="footer-link-cancellation">Cancellation &amp; Refunds</Link></li>
              <li><Link to="/insurance" className="hover:text-marigold" data-testid="footer-link-insurance">Insurance &amp; Safety</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="eyebrow text-marigold mb-5">Contact</div>
            <ul className="space-y-4 text-sm font-light text-cream/85">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-marigold" />
                <span>P-10, Tajnagri Phase 1, Tajganj,<br />Agra – 282001 (U.P.), India</span>
              </li>
              <li className="flex gap-3">
                <Phone className="w-4 h-4 mt-1 flex-shrink-0 text-marigold" />
                <a href="tel:+917017798524" className="hover:text-marigold">+91 70177 98524</a>
              </li>
              <li className="flex gap-3">
                <Mail className="w-4 h-4 mt-1 flex-shrink-0 text-marigold" />
                <a href="mailto:info@ttiholidays.com" className="hover:text-marigold">info@ttiholidays.com</a>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="w-4 h-4 mt-1 flex-shrink-0 text-marigold" />
                <a
                  href="https://wa.me/917017798524"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-marigold"
                >
                  Chat on WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-cream/60">
          <div>© {new Date().getFullYear()} Tour Trips India Holidays. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/admin/login" className="hover:text-marigold" data-testid="footer-admin-login">Admin</Link>
            <span>Made with love in Agra</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
